/** Presentation only. A host must recheck authority before any task operation. */
export type StarLearningPathNavigationV1 = Readonly<{
  schemaVersion: '1.0.0';
  themeId: string;
  pathId: string;
  pathRevisionId: string;
  title: string;
  mode: 'preview' | 'demo' | 'learning';
  currentStepId: string;
  navigation?: Readonly<{ themes: readonly Readonly<{id:string;title:string}>[]; paths: readonly Readonly<{id:string;themeId:string;title:string}>[] }>;
  steps: readonly Readonly<{ id: string; title: string; toolLabel?: string; icon?: 'course'|'leaf'|'experiment'|'inquiry'|'cube'|'world'|'results'|'video'|'memory'|'person'; state: 'preview' | 'available' | 'blocked' | 'completed' }>[];
}>;

export function validateLearningPathNavigation(value: unknown): StarLearningPathNavigationV1 {
  const exact = (v: any, keys: string[]) => v && typeof v === 'object' && !Array.isArray(v)
    && Object.keys(v).length === keys.length && keys.every(key => Object.hasOwn(v, key));
  const id = (v: unknown) => typeof v === 'string' && /^[a-z0-9][a-z0-9_-]{0,127}$/.test(v);
  const title = (v: unknown) => typeof v === 'string' && v.trim() === v && v.length > 0 && v.length <= 120;
  const data = value as StarLearningPathNavigationV1;
  if (!exact(data, ['schemaVersion','themeId','pathId','pathRevisionId','title','mode','currentStepId','steps',...(data?.navigation !== undefined ? ['navigation'] : [])])
    || data.schemaVersion !== '1.0.0' || !id(data.themeId) || !id(data.pathId)
    || !/^tlpr-[a-f0-9]{64}$/.test(data.pathRevisionId) || !title(data.title)
    || !['preview','demo','learning'].includes(data.mode) || !Array.isArray(data.steps)
    || data.steps.length < 1 || data.steps.length > 30) throw new Error('Invalid learning path navigation');
  const seen = new Set<string>();
  for (const step of data.steps) {
    if (!exact(step,['id','title','state',...(step.toolLabel !== undefined ? ['toolLabel'] : []),...(step.icon !== undefined ? ['icon'] : [])]) || (step.toolLabel !== undefined && !title(step.toolLabel)) || (step.icon !== undefined && !['course','leaf','experiment','inquiry','cube','world','results','video','memory','person'].includes(step.icon)) || !id(step.id) || !title(step.title)
      || !['preview','available','blocked','completed'].includes(step.state) || seen.has(step.id)
      || (data.mode === 'preview' && !['preview','blocked'].includes(step.state))) throw new Error('Invalid learning path step');
    seen.add(step.id);
  }
  if (!seen.has(data.currentStepId)) throw new Error('Current learning path step is missing');
  let navigation: StarLearningPathNavigationV1['navigation'];
  if (data.navigation !== undefined) {
    const nav=data.navigation;
    if (!exact(nav,['themes','paths']) || !Array.isArray(nav.themes) || !Array.isArray(nav.paths)
      || nav.themes.length<1 || nav.themes.length>30 || nav.paths.length<1 || nav.paths.length>300
      || nav.themes.some(t=>!exact(t,['id','title'])||!id(t.id)||!title(t.title))
      || new Set(nav.themes.map(t=>t.id)).size!==nav.themes.length
      || nav.paths.some(p=>!exact(p,['id','themeId','title'])||!id(p.id)||!title(p.title)||!nav.themes.some(t=>t.id===p.themeId))
      || new Set(nav.paths.map(p=>p.id)).size!==nav.paths.length
      || !nav.paths.some(p=>p.id===data.pathId&&p.themeId===data.themeId)
      || nav.themes.some(t=>!nav.paths.some(p=>p.themeId===t.id))) throw new Error('Invalid learning path choices');
    navigation=Object.freeze({themes:Object.freeze(nav.themes.map(t=>Object.freeze({...t}))),paths:Object.freeze(nav.paths.map(p=>Object.freeze({...p})))});
  }
  return Object.freeze({ ...data, ...(navigation ? {navigation} : {}), steps: Object.freeze(data.steps.map(step => Object.freeze({ ...step }))) });
}
